import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, Typography,
} from '@mui/material';
import { Feedback, FeedbackService } from '../../services/feedback.service';
import { useToast } from '../../context/ToastContext';
import { formatDateTime } from '../../utils/dateUtils';
import FeedbackDialog, { FEEDBACK_TYPES } from './FeedbackDialog';
import { FeedbackStatusChip } from './FeedbackStatusChip';
import { FeedbackAttachmentChips } from './FeedbackAttachmentChips';
import useFocusedRow from '../../hooks/useFocusedRow';

export const typeLabel = (t: string) => FEEDBACK_TYPES.find(x => x.value === t)?.label ?? t;

/** The signed-in user's own feedback with the Kervolt team's replies, newest first. */
const MyFeedbackPage: React.FC = () => {
    const toast = useToast();
    const [rows, setRows] = useState<Feedback[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [loading, setLoading] = useState(true);
    const [sendOpen, setSendOpen] = useState(false);
    // A reply notice links here with ?id=; the replied row is highlighted, not filtered.
    const { focusId } = useFocusedRow();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await FeedbackService.mine(page, size);
            setRows(res.content ?? []);
            setTotal(res.totalElements ?? 0);
        } catch (error) {
            toast.showApiError(error, 'Failed to load your feedback');
        } finally {
            setLoading(false);
        }
    }, [page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    return (
        <Box sx={{ p: 3 }} data-help-page="feedback.mine.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ flex: '1 1 240px' }}>My feedback</Typography>
                <Button variant="contained" onClick={() => setSendOpen(true)} data-help="feedback.mine.send">
                    Send feedback
                </Button>
            </Box>

            {loading && <LinearProgress aria-label="Loading" sx={{ mb: 1 }} />}
            <TableContainer component={Paper} data-help-section="feedback.mine.table">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Sent</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Message</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell data-help="feedback.mine.col.reply">Reply</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    You have not sent any feedback yet. Use the feedback button at the bottom right of any page.
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map(r => (
                            <TableRow key={r.id} selected={r.id === focusId}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.createdAt)}</TableCell>
                                <TableCell><Chip size="small" variant="outlined" label={typeLabel(r.type)} /></TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minWidth: 200 }}>
                                    {r.message}
                                    {r.pagePath && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Page: {r.pagePath}
                                        </Typography>
                                    )}
                                    <FeedbackAttachmentChips feedbackId={r.id} attachments={r.attachments} />
                                </TableCell>
                                <TableCell><FeedbackStatusChip status={r.status} /></TableCell>
                                <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', minWidth: 200 }}>
                                    {r.reply ? (
                                        <>
                                            {r.reply}
                                            {r.repliedAt && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {formatDateTime(r.repliedAt)}
                                                </Typography>
                                            )}
                                        </>
                                    ) : '—'}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
            />

            <FeedbackDialog open={sendOpen} onClose={() => { setSendOpen(false); load(); }} />
        </Box>
    );
};

export default MyFeedbackPage;
